import React from 'react'
import { Icon } from '../../../atoms/Icon'
import { PermitEvent as PermitEventType } from './event.types'
import { formatSwissPrice } from './formatUtils'
import {
  EventCard,
  EventHeader,
  EventTitle,
  EventIcon,
  EventContent,
  EventDetails,
  EventDescription,
  EventMeta,
  MetaItem,
  MetaIcon,
  EventStatus,
  EventFooter,
} from '../HistoryTab.styles'

interface PermitEventProps {
  event: PermitEventType
  translations?: {
    title?: string
    fields?: {
      permitNumber?: string
      workType?: string
      owner?: string
      architect?: string
      address?: string
      projectValue?: string
      projectArea?: string
      classification?: string
      developmentPotential?: string
    }
    status?: {
      approved?: string
      pending?: string
      rejected?: string
    }
  }
  formatPrice?: (price: number) => string
}

export const PermitEventComponent: React.FC<PermitEventProps> = ({
  event,
  translations,
  formatPrice = formatSwissPrice,
}) => {
  const getResultStatus = (result?: string) => {
    if (!result) return undefined
    const key = result.toLowerCase()
    const statusMap: Record<string, string> = {
      approved: translations?.status?.approved || 'Approved',
      pending: translations?.status?.pending || 'Pending',
      rejected: translations?.status?.rejected || 'Rejected',
    }
    return statusMap[key] || result
  }

  return (
    <EventCard $type="permit">
      <EventHeader>
        <EventTitle>
          <EventIcon>
            <Icon name="building-permit" size="small" />
          </EventIcon>
          {translations?.title || 'Building Permit'}
        </EventTitle>
        {event.result && (
          <EventStatus $status={event.result}>
            {getResultStatus(event.result)}
          </EventStatus>
        )}
      </EventHeader>

      <EventContent>
        <EventDetails>
          <EventDescription>{event.workDescription}</EventDescription>

          <EventMeta>
            {event.permitNumber && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </MetaIcon>
                {translations?.fields?.permitNumber || 'Permit'}:{' '}
                {event.permitNumber}
              </MetaItem>
            )}

            {event.workType && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 00-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z" />
                </MetaIcon>
                {translations?.fields?.workType || 'Type'}: {event.workType}
              </MetaItem>
            )}

            {event.owner && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </MetaIcon>
                {translations?.fields?.owner || 'Owner'}: {event.owner}
              </MetaItem>
            )}

            {event.architect && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12.09 2.91C10.08.9 7.07.49 4.65 1.67l4.34 4.34-3 3-4.34-4.34C.48 7.1.89 10.09 2.9 12.1c1.86 1.86 4.58 2.35 6.89 1.48l9.82 9.82 3.71-3.71-9.78-9.79c.92-2.34.44-5.1-1.45-6.99z" />
                </MetaIcon>
                {translations?.fields?.architect || 'Architect'}:{' '}
                {event.architect}
              </MetaItem>
            )}

            {event.projectValue && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1.81.45 1.61 1.67 1.61 1.16 0 1.8-.64 1.8-1.54 0-.84-.43-1.41-1.67-1.82l-1.38-.5C8.21 11.88 7.63 10.86 7.63 9.57c0-1.74 1.29-3.08 3.1-3.44V4h2.67v2.15c1.65.45 2.85 1.64 2.91 3.31h-1.96c-.07-.75-.35-1.59-1.54-1.59-1.05 0-1.54.58-1.54 1.3 0 .71.39 1.11 1.54 1.5l1.19.42c1.8.64 2.77 1.68 2.77 3.27 0 1.88-1.34 3.06-3.36 3.73z" />
                </MetaIcon>
                {translations?.fields?.projectValue || 'Value'}:{' '}
                {formatPrice(event.projectValue)}
              </MetaItem>
            )}

            {event.projectArea && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M4 4h16v16H4z" />
                </MetaIcon>
                {event.projectArea} m²
              </MetaItem>
            )}
          </EventMeta>
        </EventDetails>
      </EventContent>

      {event.classification && (
        <EventFooter>
          {translations?.fields?.classification || 'Class'}:{' '}
          {event.classification}
        </EventFooter>
      )}
    </EventCard>
  )
}
